import { type FC, useState } from 'react';

import { useGetUserProfile } from '@/api/user-dashboard/queries';
import phoneImage from '@/assets/images/get-app/phone.webp';
import AppleIcon from '@/assets/svg/get-app/apple.svg';
import GlowImage from '@/assets/svg/get-app/glow.svg';
import GooglePlayIcon from '@/assets/svg/get-app/google-play.svg';
import CloseIcon from '@/assets/svg/ui-elements/x.svg';
import { IconButton, Typography } from '@/design-system';

import { isGetAppBannerDismissed, markGetAppBannerDismissed } from './dismissal';
import {
  BannerRoot,
  CloseSlot,
  Content,
  Glow,
  PhoneFrame,
  PhoneImage,
  StoreLink,
  StoreLinks,
} from './styled';

export const APP_STORE_URL = 'https://apps.apple.com/app/gamma-prime/id6781283398';
export const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.gammaprime.app';

export const GetAppBanner: FC = () => {
  const { user } = useGetUserProfile();
  const userId = user?.id;
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);

  if (!userId || dismissedFor === userId || isGetAppBannerDismissed(userId)) return null;

  const dismiss = () => {
    markGetAppBannerDismissed(userId);
    setDismissedFor(userId);
  };

  return (
    <BannerRoot aria-label="Get the Gamma Prime app">
      <Glow aria-hidden>
        <GlowImage />
      </Glow>
      <PhoneFrame aria-hidden>
        <PhoneImage src={phoneImage} alt="" />
      </PhoneFrame>

      <Content>
        <Typography variant="body2" fontWeight={700}>
          Get the
          <br />
          Gamma Prime app
        </Typography>
        <StoreLinks>
          <StoreLink
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download on the App Store"
          >
            <AppleIcon width={24} height={24} />
          </StoreLink>
          <StoreLink
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get it on Google Play"
          >
            <GooglePlayIcon width={24} height={24} />
          </StoreLink>
        </StoreLinks>
      </Content>

      <CloseSlot>
        <IconButton variant="ghost" size="xs" color="tertiary" aria-label="Close" onClick={dismiss}>
          <CloseIcon />
        </IconButton>
      </CloseSlot>
    </BannerRoot>
  );
};
